import type {
  BarChartComponent,
  ContentCardComponent,
  DashboardComponent,
  DashboardDefinition,
  Display,
  ListComponent,
  Position,
  Styling,
  TableComponent,
  TotalsComponent,
} from 'common/types/dashboard/dashboardDefinition';
import type {
  DashboardMetricCatalogEntry,
  DashboardMetricRole,
} from 'common/types/dashboard/dashboardVocabularies';
import {
  DASHBOARD_COLUMN_VOCABULARY,
  DASHBOARD_COMPATIBILITY_REASONS,
  DASHBOARD_DIMENSIONS,
  DASHBOARD_METRIC_CATALOG,
  DASHBOARD_NUMERIC_METRICS,
  DASHBOARD_TOTALS_UNSUPPORTED_REASONS,
} from 'common/types/dashboard/dashboardVocabularies';
import { getValueType } from 'common/types/summaryDefinitions';
import type { SummaryGroupBy } from 'common/types/summaryDefinitions';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
} from 'pages/Firms/pages/FirmSummary/columnCategories';
import {
  COMPONENT_TYPE_LABELS,
  resolveColumnLabel,
} from 'pages/Intelligence/SummaryDashboard/components/DashboardGrid/dashboardComponent.helpers';
import {
  asNonEmpty,
  MAX_GRID_COLUMNS,
  MAX_LIST_LIMIT,
  MAX_TAGS,
  MIN_GRID_COLUMNS,
  MIN_LIST_LIMIT,
  readGridColumns,
  readTableSpreadsheet,
  withOptional,
  withOptionalList,
  withOptionalText,
  writeTableSpreadsheet,
} from './dashboardDraft';
import type {
  DashboardEditField,
  DashboardEditSection,
  DashboardFieldOption,
} from './dashboardEditFields.types';

const NOT_SET = '—';

// These maps stay `Record<NonNullable<…>, string>` rather than plain objects: adding an enum value
// to the backend registry regenerates the union and stops this file compiling until the new value
// gets a label. That is also why they double as the option source — a dropdown built from them can
// never silently miss an option.
const COMPARISON_LABELS: Record<NonNullable<TotalsComponent['fields']['comparison']>, string> = {
  entry: 'Entry (cost basis)',
  none: 'None',
  prior_period: 'Prior period',
};

const SERIES_SOURCE_LABELS: Record<NonNullable<BarChartComponent['fields']['series_source']>, string> = {
  financial_periods: 'Financial periods',
  measurement_dates: 'Measurement dates',
};

const ENTITY_KIND_LABELS: Record<ListComponent['fields']['entity_kind'], string> = {
  company: 'Companies',
  fund: 'Funds',
};

const DIRECTION_LABELS: Record<NonNullable<ListComponent['fields']['direction']>, string> = {
  asc: 'Ascending — lowest first',
  desc: 'Descending — highest first',
};

const NUMBER_FORMAT_LABELS: Record<NonNullable<Styling['number_format']>, string> = {
  currency: 'Currency',
  currency_compact: 'Currency (compact)',
  decimal: 'Decimal',
  integer: 'Integer',
  multiplier: 'Multiplier',
  percent: 'Percent',
};

const COLOR_SCHEME_LABELS: Record<NonNullable<Styling['color_scheme']>, string> = {
  categorical: 'Categorical',
  diverging: 'Diverging',
  sequential: 'Sequential',
};

const ORIENTATION_LABELS: Record<NonNullable<Styling['orientation']>, string> = {
  horizontal: 'Horizontal',
  vertical: 'Vertical',
};

const FILL_LABELS: Record<NonNullable<Styling['fill']>, string> = {
  area: 'Area',
  line: 'Line',
};

const BADGE_VARIANT_LABELS: Record<NonNullable<NonNullable<Display['badge']>['variant']>, string> = {
  danger: 'Danger',
  default: 'Default',
  info: 'Info',
  success: 'Success',
  warning: 'Warning',
};

const TIME_RANGE_LABELS: Record<NonNullable<NonNullable<Styling['time_range']>['default']>, string> = {
  '1Y': '1 year',
  '3M': '3 months',
  '6M': '6 months',
  ALL: 'All time',
};

const GROUP_BY_LABELS: Record<SummaryGroupBy, string> = {
  company: 'Companies',
  fund: 'Funds',
};

const SORT_DIRECTION_LABELS: Record<'asc' | 'desc', string> = {
  asc: 'Ascending',
  desc: 'Descending',
};

export const SORT_DIRECTION_OPTIONS: DashboardFieldOption[] = Object.keys(SORT_DIRECTION_LABELS).map(
  value => ({ label: SORT_DIRECTION_LABELS[value as 'asc' | 'desc'], value })
);

const optionsFromLabels = <T extends string>(labels: Record<T, string>): DashboardFieldOption[] =>
  (Object.keys(labels) as T[]).map(value => ({ label: labels[value], value }));

// Fields a picker must not offer at all, plus the values a slot currently holds. Passed in rather
// than read from a store, because the section builders are pure functions with their own tests and
// `useStore()` throws outside a StoreProvider.
export interface DashboardFieldAvailability {
  excludedFields: ReadonlySet<string>;
}

export const EMPTY_FIELD_AVAILABILITY: DashboardFieldAvailability = {
  excludedFields: new Set<string>(),
};

// Where a column would never resolve, the reason is generic; the catalog carries the specific one.
const FALLBACK_INCOMPATIBLE_REASON = 'Not available for this component type.';
const EXCLUDED_FIELD_REASON =
  'Not available with your firm’s current settings. Remove it to stop requesting it.';

// DASHBOARD_METRIC_CATALOG is generated with `satisfies Record<dimension, Entry>`, which is what
// makes tsc fail if the backend ever stops covering a dimension. That narrow key type is worth
// keeping, so reads widen here instead: one cast, and an unknown name resolves to undefined and is
// treated as fully compatible.
const catalogEntryFor = (name: string): DashboardMetricCatalogEntry | undefined =>
  (DASHBOARD_METRIC_CATALOG as Record<string, DashboardMetricCatalogEntry | undefined>)[name];

const categoryLabelFor = (name: string): string => {
  const registryCategory = getValueType(name)?.metadata?.category;
  if (registryCategory && registryCategory in CATEGORY_LABELS) {
    return CATEGORY_LABELS[registryCategory];
  }
  return catalogEntryFor(name)?.category_label ?? CATEGORY_LABELS.custom;
};

const groupRank = (name: string): number => {
  const registryCategory = getValueType(name)?.metadata?.category;
  const index = registryCategory ? CATEGORY_ORDER.indexOf(registryCategory) : -1;
  // Names the frontend registry does not carry sort after the ones it does, keeping the familiar
  // spreadsheet ordering intact at the top of the list.
  return index === -1 ? CATEGORY_ORDER.length : index;
};

const reasonFor = (name: string, code: string): string => {
  const specific = DASHBOARD_TOTALS_UNSUPPORTED_REASONS[name];
  if (specific) {
    return specific;
  }
  const template = DASHBOARD_COMPATIBILITY_REASONS[code];
  return template
    ? template.replace('{label}', resolveColumnLabel(name))
    : FALLBACK_INCOMPATIBLE_REASON;
};

interface ColumnOptionContext {
  componentType: DashboardComponent['type'];
  role: DashboardMetricRole;
  // Values the slot already holds. A stored value is never dropped from its own picker: the draft
  // saves it either way, so hiding it would make the control read "Not set" over live data.
  selected: readonly string[];
  availability: DashboardFieldAvailability;
}

const buildColumnOptions = (
  names: readonly string[],
  { componentType, role, selected, availability }: ColumnOptionContext
): DashboardFieldOption[] => {
  const selectedSet = new Set(selected);
  const options: DashboardFieldOption[] = [];

  names.forEach(value => {
    const isSelected = selectedSet.has(value);
    const group = categoryLabelFor(value);

    if (availability.excludedFields.has(value)) {
      // Hidden, not disabled — unless it is already stored, in which case it has to stay visible.
      if (isSelected) {
        options.push({
          disabledReason: EXCLUDED_FIELD_REASON,
          group,
          isLegacy: true,
          label: resolveColumnLabel(value),
          value,
        });
      }
      return;
    }

    // Absent from the catalog means fully compatible. Failing open keeps a metadata gap from ever
    // making the picker narrower than it is today.
    const code = catalogEntryFor(value)?.compatibility[role]?.incompatible[componentType];
    options.push({
      ...(code ? { disabledReason: reasonFor(value, code) } : {}),
      ...(code && isSelected ? { isLegacy: true } : {}),
      group,
      label: resolveColumnLabel(value),
      value,
    });
  });

  // Group order first, then selectable before disabled, then label. The middle key matters because
  // DashboardEnumSelect and the picker both window the list to the first N options: without it a
  // long run of disabled rows would fill the window.
  return options.sort((a, b) => {
    const rank = groupRank(a.value) - groupRank(b.value);
    if (rank !== 0) {
      return rank;
    }
    const selectable = Number(Boolean(a.disabledReason)) - Number(Boolean(b.disabledReason));
    if (selectable !== 0) {
      return selectable;
    }
    return a.label.localeCompare(b.label);
  });
};

// Over `company_name` each bar is one raw company value. Every other dimension aggregates rows
// within a category — notably `fund_name`, which combines all companies held by the fund.
const measureRoleForDimension = (dimension: string): DashboardMetricRole =>
  dimension === 'company_name' ? 'column' : 'measure';

type ComponentPatch = (next: DashboardComponent) => void;

// display / styling / position are declared identically on all seven component interfaces, so
// patching them is type-safe in fact but not provably so to TS. One cast here beats seven arms.
const patchEnvelope = (
  component: DashboardComponent,
  patch: { display?: Display; position?: Position; styling?: Styling },
  onChange: ComponentPatch
): void => {
  onChange({ ...component, ...patch } as DashboardComponent);
};

const patchDisplay = (
  component: DashboardComponent,
  next: Display,
  onChange: ComponentPatch
): void => patchEnvelope(component, { display: next }, onChange);

const patchStyling = (
  component: DashboardComponent,
  next: Styling,
  onChange: ComponentPatch
): void => patchEnvelope(component, { styling: next }, onChange);

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

const buildDisplayFields = (
  component: DashboardComponent,
  onChange: ComponentPatch
): DashboardEditField[] => {
  const display = component.display ?? {};
  const set = (next: Display) => patchDisplay(component, next, onChange);

  return [
    {
      kind: 'text',
      key: 'display.title',
      label: 'Title',
      value: display.title ?? '',
      onChange: next => set(withOptionalText(display, 'title', next)),
    },
    {
      kind: 'text',
      key: 'display.subtitle',
      label: 'Subtitle',
      value: display.subtitle ?? '',
      onChange: next => set(withOptionalText(display, 'subtitle', next)),
    },
    {
      kind: 'textarea',
      key: 'display.description',
      label: 'Description',
      value: display.description ?? '',
      onChange: next => set(withOptionalText(display, 'description', next)),
    },
    {
      kind: 'text',
      key: 'display.badge.text',
      label: 'Badge text',
      // Clearing the text removes the whole badge, variant included — the backend discards a badge
      // with no text, so keeping an orphan variant would only look like it had been saved.
      helperText: 'Clearing this removes the badge.',
      value: display.badge?.text ?? '',
      onChange: next =>
        set(
          next.trim()
            ? { ...display, badge: { ...display.badge, text: next } }
            : withOptional(display, 'badge', undefined)
        ),
    },
    {
      kind: 'select',
      key: 'display.badge.variant',
      label: 'Badge style',
      options: optionsFromLabels(BADGE_VARIANT_LABELS),
      value: display.badge?.variant,
      onChange: next =>
        set(
          display.badge?.text
            ? {
              ...display,
              badge: withOptional(
                { ...display.badge, text: display.badge.text },
                'variant',
                next as NonNullable<Display['badge']>['variant']
              ),
            }
            : display
        ),
    },
    {
      kind: 'text',
      key: 'display.highlight.value',
      label: 'Highlight',
      helperText: 'Clearing this removes the highlight.',
      value: display.highlight?.value ?? '',
      onChange: next =>
        set(
          next.trim()
            ? { ...display, highlight: { ...display.highlight, value: next } }
            : withOptional(display, 'highlight', undefined)
        ),
    },
    {
      kind: 'text',
      key: 'display.highlight.caption',
      label: 'Highlight caption',
      value: display.highlight?.caption ?? '',
      onChange: next =>
        set(
          display.highlight?.value
            ? {
              ...display,
              highlight: withOptionalText(
                { ...display.highlight, value: display.highlight.value },
                'caption',
                next
              ),
            }
            : display
        ),
    },
    {
      kind: 'stringList',
      key: 'display.tags',
      label: 'Tags',
      maxItems: MAX_TAGS,
      placeholder: 'Add a tag',
      value: display.tags ?? [],
      onChange: next => set(withOptionalList(display, 'tags', next)),
    },
    {
      kind: 'text',
      key: 'display.action.label',
      label: 'Action label',
      helperText: 'Clearing this removes the action link.',
      value: display.action?.label ?? '',
      onChange: next =>
        set(
          next.trim()
            ? { ...display, action: { ...display.action, label: next } }
            : withOptional(display, 'action', undefined)
        ),
    },
    {
      kind: 'text',
      key: 'display.action.href',
      label: 'Action link',
      value: display.action?.href ?? '',
      onChange: next =>
        set(
          display.action?.label
            ? {
              ...display,
              action: withOptionalText(
                { ...display.action, label: display.action.label },
                'href',
                next
              ),
            }
            : display
        ),
    },
  ];
};

// ---------------------------------------------------------------------------
// Styling
// ---------------------------------------------------------------------------

const buildStylingFields = (
  component: DashboardComponent,
  onChange: ComponentPatch
): DashboardEditField[] => {
  const styling = component.styling ?? {};
  const set = (next: Styling) => patchStyling(component, next, onChange);
  const timeRange = styling.time_range ?? {};

  return [
    {
      kind: 'select',
      key: 'styling.number_format',
      label: 'Number format',
      options: optionsFromLabels(NUMBER_FORMAT_LABELS),
      value: styling.number_format,
      onChange: next =>
        set(withOptional(styling, 'number_format', next as Styling['number_format'])),
    },
    {
      kind: 'boolean',
      key: 'styling.accent_bar',
      label: 'Accent bar',
      value: styling.accent_bar,
      onChange: next => set(withOptional(styling, 'accent_bar', next)),
    },
    {
      kind: 'boolean',
      key: 'styling.show_trend_icon',
      label: 'Trend icon',
      value: styling.show_trend_icon,
      onChange: next => set(withOptional(styling, 'show_trend_icon', next)),
    },
    {
      kind: 'select',
      key: 'styling.color_scheme',
      label: 'Colour scheme',
      options: optionsFromLabels(COLOR_SCHEME_LABELS),
      value: styling.color_scheme,
      onChange: next => set(withOptional(styling, 'color_scheme', next as Styling['color_scheme'])),
    },
    {
      kind: 'colorList',
      key: 'styling.colors',
      label: 'Colours',
      helperText: 'Series take these in order. Leave empty for the default palette.',
      value: styling.colors ?? [],
      onChange: next => set(withOptionalList(styling, 'colors', next)),
    },
    {
      kind: 'boolean',
      key: 'styling.show_legend',
      label: 'Legend',
      value: styling.show_legend,
      onChange: next => set(withOptional(styling, 'show_legend', next)),
    },
    {
      kind: 'select',
      key: 'styling.orientation',
      label: 'Orientation',
      options: optionsFromLabels(ORIENTATION_LABELS),
      value: styling.orientation,
      onChange: next => set(withOptional(styling, 'orientation', next as Styling['orientation'])),
    },
    {
      kind: 'select',
      key: 'styling.fill',
      label: 'Fill',
      options: optionsFromLabels(FILL_LABELS),
      value: styling.fill,
      onChange: next => set(withOptional(styling, 'fill', next as Styling['fill'])),
    },
    {
      kind: 'multiSelect',
      key: 'styling.time_range.options',
      label: 'Time ranges',
      options: optionsFromLabels(TIME_RANGE_LABELS),
      value: timeRange.options ?? [],
      onChange: next => {
        const options = next as NonNullable<Styling['time_range']>['options'];
        const nextRange = withOptionalList({ ...timeRange }, 'options', next);
        // The whole key is dropped once nothing survives, matching the validator, which omits
        // time_range entirely rather than storing an empty object.
        set(
          Object.keys(nextRange).length
            ? { ...styling, time_range: { ...nextRange, options } }
            : withOptional(styling, 'time_range', undefined)
        );
      },
    },
    {
      kind: 'select',
      key: 'styling.time_range.default',
      label: 'Default time range',
      options: optionsFromLabels(TIME_RANGE_LABELS),
      value: timeRange.default,
      onChange: next => {
        const nextRange = withOptional(
          { ...timeRange },
          'default',
          next as NonNullable<Styling['time_range']>['default']
        );
        set(
          Object.keys(nextRange).length
            ? { ...styling, time_range: nextRange }
            : withOptional(styling, 'time_range', undefined)
        );
      },
    },
    {
      kind: 'text',
      key: 'styling.variant',
      label: 'Variant',
      helperText: 'Free-form hint, e.g. compact or wide.',
      value: styling.variant ?? '',
      onChange: next => set(withOptionalText(styling, 'variant', next)),
    },
  ];
};

// ---------------------------------------------------------------------------
// Per-type data fields
// ---------------------------------------------------------------------------

const buildTableFields = (
  component: TableComponent,
  onChange: ComponentPatch,
  availability: DashboardFieldAvailability
): DashboardEditField[] => {
  const spreadsheet = readTableSpreadsheet(component.fields);
  const set = (next: Partial<typeof spreadsheet>) =>
    onChange(writeTableSpreadsheet(component, { ...spreadsheet, ...next }));

  // Sort and filter columns are restricted to the columns actually on the table: a sort key
  // referencing a column the table does not show has nothing to sort by on screen.
  const selectedColumnOptions = buildColumnOptions(spreadsheet.columns, {
    availability,
    componentType: 'table',
    role: 'column',
    selected: spreadsheet.columns,
  });

  return [
    {
      kind: 'orderedList',
      key: 'fields.columns',
      label: 'Columns',
      addLabel: 'Add column',
      minItems: 1,
      options: buildColumnOptions(DASHBOARD_COLUMN_VOCABULARY, {
        availability,
        componentType: 'table',
        role: 'column',
        selected: spreadsheet.columns,
      }),
      value: spreadsheet.columns,
      onChange: columns => set({ columns }),
    },
    {
      kind: 'select',
      key: 'fields.groupBy',
      label: 'Group by',
      isRequired: true,
      options: optionsFromLabels(GROUP_BY_LABELS),
      value: spreadsheet.groupBy,
      onChange: next => set({ groupBy: next === 'fund' ? 'fund' : 'company' }),
    },
    {
      kind: 'sortKeys',
      key: 'fields.sortKeys',
      label: 'Sort',
      helperText: 'Rows are sorted by each key in order.',
      options: selectedColumnOptions,
      value: spreadsheet.sortKeys,
      onChange: sortKeys => set({ sortKeys }),
    },
    {
      kind: 'filters',
      key: 'fields.filters',
      label: 'Filters',
      options: selectedColumnOptions,
      value: spreadsheet.filters,
      onChange: filters => set({ filters }),
    },
  ];
};

const buildDataFields = (
  component: DashboardComponent,
  onChange: ComponentPatch,
  availability: DashboardFieldAvailability
): DashboardEditField[] => {
  switch (component.type) {
    case 'totals': {
      const { fields } = component;

      return [
        {
          kind: 'orderedList',
          key: 'fields.metrics',
          label: 'Metrics',
          addLabel: 'Add metric',
          minItems: 1,
          options: buildColumnOptions(DASHBOARD_NUMERIC_METRICS, {
            availability,
            componentType: 'totals',
            role: 'measure',
            selected: fields.metrics,
          }),
          value: fields.metrics,
          onChange: next => {
            const metrics = asNonEmpty(next as TotalsComponent['fields']['metrics']);
            if (metrics) {
              onChange({ ...component, fields: { ...fields, metrics } });
            }
          },
        },
        {
          kind: 'select',
          key: 'fields.comparison',
          label: 'Comparison',
          options: optionsFromLabels(COMPARISON_LABELS),
          value: fields.comparison,
          onChange: next =>
            onChange({
              ...component,
              fields: withOptional(
                fields,
                'comparison',
                next as TotalsComponent['fields']['comparison']
              ),
            }),
        },
      ];
    }
    case 'bar_chart':
    case 'line_chart': {
      const { fields } = component;

      return [
        {
          kind: 'select',
          key: 'fields.dimension',
          label: 'Dimension',
          isRequired: true,
          searchable: true,
          options: buildColumnOptions(DASHBOARD_DIMENSIONS, {
            availability,
            componentType: component.type,
            role: 'dimension',
            selected: [fields.dimension],
          }),
          value: fields.dimension,
          onChange: next => {
            if (next) {
              onChange({
                ...component,
                fields: { ...fields, dimension: next as BarChartComponent['fields']['dimension'] },
              });
            }
          },
        },
        {
          kind: 'orderedList',
          key: 'fields.metrics',
          label: 'Metrics',
          addLabel: 'Add metric',
          minItems: 1,
          options: buildColumnOptions(DASHBOARD_NUMERIC_METRICS, {
            availability,
            componentType: component.type,
            role: measureRoleForDimension(fields.dimension),
            selected: fields.metrics,
          }),
          value: fields.metrics,
          onChange: next => {
            const metrics = asNonEmpty(next as BarChartComponent['fields']['metrics']);
            if (metrics) {
              onChange({ ...component, fields: { ...fields, metrics } });
            }
          },
        },
        {
          kind: 'orderedList',
          key: 'fields.series',
          label: 'Series',
          addLabel: 'Add series',
          options: buildColumnOptions(DASHBOARD_NUMERIC_METRICS, {
            availability,
            componentType: component.type,
            role: measureRoleForDimension(fields.dimension),
            selected: fields.series ?? [],
          }),
          value: fields.series ?? [],
          onChange: next =>
            onChange({
              ...component,
              fields: withOptionalList(fields, 'series', next),
            }),
        },
        {
          kind: 'select',
          key: 'fields.series_source',
          label: 'Series source',
          options: optionsFromLabels(SERIES_SOURCE_LABELS),
          value: fields.series_source,
          onChange: next =>
            onChange({
              ...component,
              fields: withOptional(
                fields,
                'series_source',
                next as BarChartComponent['fields']['series_source']
              ),
            }),
        },
      ];
    }
    case 'pie_chart': {
      const { fields } = component;

      return [
        {
          kind: 'select',
          key: 'fields.dimension',
          label: 'Dimension',
          isRequired: true,
          searchable: true,
          options: buildColumnOptions(DASHBOARD_DIMENSIONS, {
            availability,
            componentType: 'pie_chart',
            role: 'dimension',
            selected: [fields.dimension],
          }),
          value: fields.dimension,
          onChange: next => {
            if (next) {
              onChange({
                ...component,
                fields: { ...fields, dimension: next as typeof fields.dimension },
              });
            }
          },
        },
        {
          kind: 'select',
          key: 'fields.metric',
          label: 'Metric',
          isRequired: true,
          searchable: true,
          options: buildColumnOptions(DASHBOARD_NUMERIC_METRICS, {
            availability,
            componentType: 'pie_chart',
            role: 'measure',
            selected: [fields.metric],
          }),
          value: fields.metric,
          onChange: next => {
            if (next) {
              onChange({
                ...component,
                fields: { ...fields, metric: next as typeof fields.metric },
              });
            }
          },
        },
      ];
    }
    case 'table':
      return buildTableFields(component, onChange, availability);
    case 'content_card': {
      const fields = component.fields ?? {};

      return [
        {
          kind: 'select',
          key: 'fields.metric',
          label: 'Metric',
          // Deliberately the full column vocabulary, not the numeric subset: a content card's
          // metric backs its highlight text and is not narrowed by the registry. The backend spec
          // declares no numeric field keys for this type, so the `column` role disables nothing
          // here — the breadth is now preserved by construction rather than by exception.
          searchable: true,
          options: buildColumnOptions(DASHBOARD_COLUMN_VOCABULARY, {
            availability,
            componentType: 'content_card',
            role: 'measure',
            selected: fields.metric ? [fields.metric] : [],
          }),
          value: fields.metric,
          onChange: next => {
            const nextFields = withOptional(
              fields,
              'metric',
              next as NonNullable<ContentCardComponent['fields']>['metric']
            );
            onChange(
              Object.keys(nextFields).length
                ? { ...component, fields: nextFields }
                : { ...component, fields: undefined }
            );
          },
        },
      ];
    }
    case 'list': {
      const { fields } = component;

      return [
        {
          kind: 'select',
          key: 'fields.metric',
          label: 'Metric',
          isRequired: true,
          searchable: true,
          // `measure` like every other metric slot. That a ranked list combines nothing — so a
          // per-share price is fine to rank by — is stated in the backend catalog, not worked
          // around here by asking for a role this component does not declare.
          options: buildColumnOptions(DASHBOARD_NUMERIC_METRICS, {
            availability,
            componentType: 'list',
            role: 'measure',
            selected: [fields.metric],
          }),
          value: fields.metric,
          onChange: next => {
            if (next) {
              onChange({
                ...component,
                fields: { ...fields, metric: next as typeof fields.metric },
              });
            }
          },
        },
        {
          kind: 'select',
          key: 'fields.entity_kind',
          label: 'Ranked over',
          isRequired: true,
          options: optionsFromLabels(ENTITY_KIND_LABELS),
          value: fields.entity_kind,
          onChange: next => {
            if (next) {
              onChange({
                ...component,
                fields: { ...fields, entity_kind: next as typeof fields.entity_kind },
              });
            }
          },
        },
        {
          kind: 'select',
          key: 'fields.direction',
          label: 'Direction',
          options: optionsFromLabels(DIRECTION_LABELS),
          value: fields.direction,
          onChange: next =>
            onChange({
              ...component,
              fields: withOptional(fields, 'direction', next as typeof fields.direction),
            }),
        },
        {
          kind: 'number',
          key: 'fields.limit',
          label: 'Limit',
          // The one optional number in the dialog: clearing it drops the key and the backend
          // default applies, so it is the only one where an empty box is a real edit.
          helperText: `Rows shown, between ${MIN_LIST_LIMIT} and ${MAX_LIST_LIMIT}. Empty uses the default.`,
          min: MIN_LIST_LIMIT,
          max: MAX_LIST_LIMIT,
          value: fields.limit,
          onChange: next =>
            onChange({ ...component, fields: withOptional(fields, 'limit', next) }),
        },
      ];
    }
    default: {
      // Exhaustive by construction: a component type added to the backend registry regenerates
      // DashboardComponent and stops this file compiling until it gets an editor.
      const unhandled: never = component;

      return unhandled;
    }
  }
};

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

const buildLayoutFields = (
  component: DashboardComponent,
  gridColumns: number,
  onChange: ComponentPatch
): DashboardEditField[] => {
  const position = component.position ?? {};
  const set = (next: Position) => patchEnvelope(component, { position: next }, onChange);

  // Only width and height are editable: x and y are re-derived by the shelf-packing pass after every
  // structural change, so hand-set coordinates would be overwritten the next time anything moved.
  return [
    {
      kind: 'number',
      key: 'position.w',
      label: 'Width',
      isRequired: true,
      min: 1,
      max: gridColumns,
      helperText: `Columns spanned, out of ${gridColumns}.`,
      value: position.w ?? 1,
      onChange: next => set({ ...position, w: next ?? 1 }),
    },
    {
      kind: 'number',
      key: 'position.h',
      label: 'Height',
      isRequired: true,
      min: 1,
      value: position.h ?? 1,
      onChange: next => set({ ...position, h: next ?? 1 }),
    },
  ];
};

export const buildComponentSections = (
  component: DashboardComponent,
  gridColumns: number,
  onChange: ComponentPatch,
  // Defaults to a shared frozen singleton so the identity is stable across renders and callers that
  // do not gate fields (the dialog's own tests among them) behave exactly as before.
  availability: DashboardFieldAvailability = EMPTY_FIELD_AVAILABILITY
): DashboardEditSection[] => [
  {
    title: 'Component',
    fields: [
      { kind: 'readOnly', key: 'type', label: 'Type', value: COMPONENT_TYPE_LABELS[component.type] },
      { kind: 'readOnly', key: 'id', label: 'Component ID', value: component.id ?? NOT_SET },
    ],
  },
  { title: 'Display', fields: buildDisplayFields(component, onChange) },
  { title: 'Data', fields: buildDataFields(component, onChange, availability) },
  { title: 'Styling', fields: buildStylingFields(component, onChange) },
  { title: 'Layout', fields: buildLayoutFields(component, gridColumns, onChange) },
];

interface DashboardSectionsParams {
  definition: DashboardDefinition;
  name: string;
  onLayoutColumnsChange: (columns: number) => void;
  onNameChange: (name: string) => void;
}

export const buildDashboardSections = ({
  definition,
  name,
  onLayoutColumnsChange,
  onNameChange,
}: DashboardSectionsParams): DashboardEditSection[] => [
  {
    title: 'Dashboard',
    fields: [
      {
        kind: 'text',
        key: 'name',
        label: 'Name',
        value: name,
        onChange: onNameChange,
      },
      {
        kind: 'number',
        key: 'layout.columns',
        label: 'Grid columns',
        isRequired: true,
        min: MIN_GRID_COLUMNS,
        max: MAX_GRID_COLUMNS,
        helperText: 'Tiles re-flow to fit whenever this changes.',
        value: readGridColumns(definition),
        onChange: next => onLayoutColumnsChange(next ?? readGridColumns(definition)),
      },
      {
        kind: 'readOnly',
        key: 'version',
        label: 'Schema version',
        value: String(definition.version),
      },
    ],
  },
  {
    title: 'Prompt',
    fields: [
      {
        kind: 'readOnly',
        key: 'prompt',
        label: 'Prompt',
        // Read-only on purpose: the prompt bar on the dashboard tab owns the brief, and submitting
        // there regenerates the components. Editing it here would let the two silently disagree.
        value: definition.prompt?.trim() || NOT_SET,
      },
    ],
  },
];
